/**
 * Head-to-head competitor comparisons (wave 2 commercial pages): `/comparisons/<a>-vs-<b>`.
 *
 * Every fact comes from the official vendor pages in `sourceUrls`, checked on `lastVerified`. Starting prices are
 * read from `competitor-pricing.ts` so they never drift from the pricing pages; draft (unverified) pricing is never
 * shown as a number.
 *
 * Kreatli copy uses verified features only: no comment export, website proofing, white label, storage
 * integrations, editing-software panels or frame stepping.
 */
import {
  type CompetitorId,
  type CompetitorPricing,
  competitorPricingPath,
  describeStartingPrice,
  getCompetitorPricing,
  hasPublishedPricingPage,
} from './competitor-pricing';
import type { PseoEntryBase } from './pseo/types';
import { isPublishable } from './pseo/types';

export interface CompetitorVsRow {
  label: string;
  a: string;
  b: string;
}

export interface CompetitorVsEntry extends PseoEntryBase {
  a: CompetitorId;
  b: CompetitorId;
  /** One-paragraph answer shown at the top of the page. */
  summary: string;
  rows: CompetitorVsRow[];
  whenAIsBetter: string[];
  whenBIsBetter: string[];
  whereKreatliFits: string[];
}

const VERIFIED_ON = '2026-10-21';
const INITIAL_CHANGELOG = [{ date: VERIFIED_ON, change: 'Initial comparison from official vendor pages.' }];

/** Starting price from the pricing registry; draft entries show the pricing basis and send readers to the vendor. */
function startingPriceCell(id: CompetitorId): string {
  const entry = getCompetitorPricing(id);
  if (isPublishable(entry)) {
    return describeStartingPrice(entry);
  }
  return entry.basis === 'add-on'
    ? `Per-user add-on; price varies by region. Check ${entry.vendor} pricing for your country.`
    : `Check ${entry.vendor} for current pricing.`;
}

const FRAME_IO = {
  positioning: 'Video-first review and collaboration, part of Adobe',
  fileTypes: 'Video, images, documents and Office files',
  integrations:
    'Premiere Pro panel (V4, 25.6 and later), After Effects, Lightroom, LumaFusion and Baselight. The DaVinci Resolve integration is legacy and Studio-only',
  guestReview: 'Yes. Share reviewers comment without an account (name and email after the first comment)',
  approvals: 'Review through Shares and comments',
  pricingBasis: 'Per member (Free up to 2, Pro up to 5, Team up to 15)',
  freePlan: 'Yes: 2 members, 2GB (+2GB mounted), 2 projects, 1 workspace',
};

const FRAME_IO_SOURCES = [
  { label: 'Frame.io pricing', url: 'https://www.frame.io/pricing' },
  { label: 'Frame.io integrations', url: 'https://frame.io/integrations' },
  {
    label: 'Frame.io help: updating to V4',
    url: 'https://help.frame.io/en/articles/11534393-what-to-expect-when-updating-to-v4-a-comprehensive-guide-for-updating-your-account-on-a-free-pro-or-team-plan',
  },
  {
    label: 'Frame.io help: DaVinci Resolve Studio integration (legacy)',
    url: 'https://help.frame.io/en/articles/2883263-davinci-resolve-studio-17-integration-legacy',
  },
];

const KREATLI_FITS_COMMON = [
  'Kreatli reviews video, images, PDFs and audio in one project, with timecoded and time-range comments, drawn markup and PDF page comments.',
  'Guests review from a link without logging in (name only), on every plan.',
  'Custom approval statuses are visible to guests on the shared link, and version stacks with version compare show what changed between rounds.',
  'Tasks, deliverables and chat sit next to the review, so the work around a round stays in the same place.',
  'Kreatli has no editing-software panels, no comment export and no website proofing. If you need those, one of the tools on this page is a better fit.',
];

export const COMPETITOR_COMPARISONS: CompetitorVsEntry[] = [
  {
    slug: 'frame-io-vs-filestage',
    primaryQuery: 'frame.io vs filestage',
    a: 'frame-io',
    b: 'filestage',
    summary:
      'Frame.io is a video-first review tool priced per member, with deep Adobe integrations. Filestage is an online proofing platform for many formats, including live websites, priced as a flat monthly fee in EUR with 10 seats included. Pick Frame.io if your team edits in Premiere Pro; pick Filestage if you proof many file types with structured approvals.',
    rows: [
      { label: 'Positioning', a: FRAME_IO.positioning, b: 'Automated online proofing platform' },
      { label: 'File types', a: FRAME_IO.fileTypes, b: 'Video, designs, PDFs, documents, live websites and HTML' },
      { label: 'Editing-software integrations', a: FRAME_IO.integrations, b: 'Premiere Pro, Photoshop and InDesign' },
      { label: 'Guest review without an account', a: FRAME_IO.guestReview, b: 'Yes. Unlimited reviewers, no account needed' },
      { label: 'Approvals', a: FRAME_IO.approvals, b: 'Approve or request changes, with due dates and reminders' },
      { label: 'Pricing basis', a: FRAME_IO.pricingBasis, b: 'Flat monthly price, 10 seats included; extra seats in packs of 5' },
      { label: 'Starting price', a: startingPriceCell('frame-io'), b: startingPriceCell('filestage') },
      {
        label: 'Free plan',
        a: FRAME_IO.freePlan,
        b: 'Yes: 1 active project, 5 new files per month, 2 reviewer groups, 2GB',
      },
    ],
    whenAIsBetter: [
      'Your editors work in Premiere Pro or After Effects and want review inside the app.',
      'You need to export comments to CSV or plain text for your editing software, or print them to PDF.',
      'Your team is small and you’d rather pay per member than a flat fee that covers 10 seats.',
    ],
    whenBIsBetter: [
      'You proof live websites, HTML, designs and documents as well as video.',
      'You want approve/request-changes decisions with due dates and reminders.',
      'Your team is close to 10 people: Filestage’s flat price includes 10 seats.',
    ],
    whereKreatliFits: KREATLI_FITS_COMMON,
    faqs: [
      {
        question: 'Is Frame.io or Filestage better for video review?',
        answer:
          'Frame.io is built around video and integrates with Premiere Pro (V4 panel, 25.6 and later) and After Effects. Filestage reviews video too, but its strength is proofing many formats, including live websites and HTML, with approve or request-changes decisions.',
      },
      {
        question: 'Which is cheaper, Frame.io or Filestage?',
        answer:
          'It depends on team size and currency. Frame.io Pro is $15 per member per month for up to 5 members. Filestage Starter is a flat €199 per month with 10 seats included. For 3 members, Frame.io Pro is $45 per month; Filestage Starter stays €199 for any team of up to 10.',
      },
      {
        question: 'Can clients review without an account in Frame.io and Filestage?',
        answer:
          'Yes. Frame.io share reviewers can comment without an account and are asked for a name and email after the first comment. Filestage lists unlimited reviewers who don’t need an account.',
      },
      {
        question: 'Do Frame.io and Filestage have free plans?',
        answer:
          'Both do. Frame.io Free allows 2 members, 2GB (+2GB mounted) and 2 projects. Filestage Free allows 1 active project, 5 new files per month and 2 reviewer groups.',
      },
      {
        question: 'Where does Kreatli fit between Frame.io and Filestage?',
        answer:
          'Kreatli is for creative teams that review video, images, PDFs and audio with clients and run tasks and deliverables in the same project. Guests review without logging in on every plan. Kreatli has no editing-software panels, comment export or website proofing.',
      },
    ],
    sourceUrls: [
      ...FRAME_IO_SOURCES,
      { label: 'Filestage', url: 'https://filestage.io/' },
      { label: 'Filestage pricing', url: 'https://filestage.io/pricing' },
    ],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
  },
  {
    slug: 'frame-io-vs-wipster',
    primaryQuery: 'frame.io vs wipster',
    a: 'frame-io',
    b: 'wipster',
    summary:
      'Frame.io and Wipster are both video review tools priced per user. Frame.io leans on Adobe integrations and caps listed plans at 15 members. Wipster adds panels for Final Cut Pro as well as Premiere Pro and After Effects, multi-step approvals with an audit trail, and a Team plan with no user limit.',
    rows: [
      { label: 'Positioning', a: FRAME_IO.positioning, b: 'Review and approval platform' },
      { label: 'File types', a: FRAME_IO.fileTypes, b: 'Video, images, PDF and audio; “no file size limits”' },
      { label: 'Editing-software integrations', a: FRAME_IO.integrations, b: 'Premiere Pro, Final Cut Pro and After Effects panels' },
      { label: 'Guest review without an account', a: FRAME_IO.guestReview, b: 'Yes. Free, unlimited reviewers' },
      { label: 'Approvals', a: FRAME_IO.approvals, b: 'Multi-step approvals, audit trail and one-click sign-off' },
      { label: 'Pricing basis', a: FRAME_IO.pricingBasis, b: 'Per user on Team (no user limit); Light is one monthly price' },
      { label: 'Starting price', a: startingPriceCell('frame-io'), b: startingPriceCell('wipster') },
      { label: 'Free plan', a: FRAME_IO.freePlan, b: 'No free plan on its pricing page; trial without a credit card' },
    ],
    whenAIsBetter: [
      'You want a free plan to start: Frame.io Free covers 2 members and 2 projects; Wipster lists no free plan.',
      'You work in Adobe tools beyond Premiere Pro, such as Lightroom, or use LumaFusion or Baselight.',
      'You need comment export to CSV or plain text for editing software, or Print Comments to PDF.',
    ],
    whenBIsBetter: [
      'Your editors use Final Cut Pro and want a panel inside it.',
      'You need multi-step approvals with an audit trail and one-click sign-off.',
      'Your team is larger than 15: Wipster Team has no user limit, while Frame.io above 15 members is Enterprise.',
    ],
    whereKreatliFits: KREATLI_FITS_COMMON,
    faqs: [
      {
        question: 'What is the difference between Frame.io and Wipster?',
        answer:
          'Both are video review tools priced per user. Frame.io focuses on Adobe integrations and has a free plan. Wipster has Premiere Pro, Final Cut Pro and After Effects panels, multi-step approvals with an audit trail, and a Team plan with no user limit.',
      },
      {
        question: 'Is Frame.io or Wipster cheaper?',
        answer:
          'Frame.io Pro is $15 per member per month for up to 5 members; Team is $25 for up to 15. Wipster Team is $25 per user per month, or $19.95 billed annually, with no user limit. Wipster Light is $11.95 per month.',
      },
      {
        question: 'Does Wipster have a free plan like Frame.io?',
        answer:
          'Wipster’s pricing page lists no free plan; it offers a trial without a credit card. Frame.io has a Free plan for up to 2 members, 2GB (+2GB mounted) and 2 projects.',
      },
      {
        question: 'Which works with Final Cut Pro?',
        answer:
          'Wipster lists a Final Cut Pro panel. Frame.io’s current integrations page lists Premiere Pro, After Effects, Lightroom, LumaFusion and Baselight.',
      },
      {
        question: 'Where does Kreatli fit between Frame.io and Wipster?',
        answer:
          'Kreatli suits creative teams that review video alongside images, PDFs and audio, with guest links on every plan and tasks and deliverables in the same project. It has no editing-software panels or comment export.',
      },
    ],
    sourceUrls: [
      ...FRAME_IO_SOURCES,
      { label: 'Wipster', url: 'https://wipster.io/' },
      { label: 'Wipster pricing', url: 'https://wipster.io/pricing' },
    ],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
  },
  {
    slug: 'dropbox-replay-vs-frame-io',
    primaryQuery: 'dropbox replay vs frame.io',
    a: 'dropbox-replay',
    b: 'frame-io',
    summary:
      'Dropbox Replay is a review add-on for teams already on Dropbox Plus or higher, with integrations for Premiere Pro, Final Cut Pro, DaVinci Resolve, After Effects and Pro Tools. Frame.io is a standalone, video-first review tool priced per member, with a free plan and deep Adobe integrations.',
    rows: [
      { label: 'Positioning', a: 'Review and approvals inside Dropbox', b: FRAME_IO.positioning },
      { label: 'File types', a: 'Video, images, audio, PSD and PDF', b: FRAME_IO.fileTypes },
      {
        label: 'Editing-software integrations',
        a: 'Premiere Pro, Final Cut Pro, DaVinci Resolve, After Effects and Pro Tools',
        b: FRAME_IO.integrations,
      },
      { label: 'Guest review without an account', a: 'Yes. Reviewers don’t need a Dropbox account', b: FRAME_IO.guestReview },
      { label: 'Approvals', a: 'Status tracking; due dates need the add-on', b: FRAME_IO.approvals },
      {
        label: 'Pricing basis',
        a: 'Per-user add-on to a Dropbox plan (Plus or higher, not Family)',
        b: FRAME_IO.pricingBasis,
      },
      { label: 'Starting price', a: startingPriceCell('dropbox-replay'), b: startingPriceCell('frame-io') },
      {
        label: 'Free plan',
        a: 'Limited: 4 or 10 file uploads without the add-on, depending on the Dropbox plan',
        b: FRAME_IO.freePlan,
      },
    ],
    whenAIsBetter: [
      'Your files already live in Dropbox and you’re on Plus or higher.',
      'You edit in DaVinci Resolve or mix audio in Pro Tools: Replay lists integrations for both, while Frame.io’s Resolve integration is legacy and Studio-only.',
      'You upload very large files: Replay accepts uploads up to 150GB and video up to 12 hours.',
    ],
    whenBIsBetter: [
      'You don’t use Dropbox and want a standalone review tool with a free plan.',
      'You want review inside Premiere Pro through the V4 panel, or Lightroom, LumaFusion or Baselight integrations.',
      'You need comment export to CSV or plain text for editing software, or Print Comments to PDF.',
    ],
    whereKreatliFits: KREATLI_FITS_COMMON,
    faqs: [
      {
        question: 'Is Dropbox Replay a replacement for Frame.io?',
        answer:
          'For teams already on Dropbox Plus or higher, it can be. Replay reviews video, images, audio, PSD and PDF and integrates with Premiere Pro, Final Cut Pro, DaVinci Resolve, After Effects and Pro Tools. Frame.io is a standalone tool with a free plan and deeper Adobe integrations.',
      },
      {
        question: 'How is Dropbox Replay priced compared with Frame.io?',
        answer:
          'Replay is a per-user add-on on top of a Dropbox plan, and its price varies by region, so check Dropbox for yours. Frame.io is priced per member: Pro is $15 and Team is $25 per member per month, plus tax.',
      },
      {
        question: 'Can clients review without an account in Dropbox Replay and Frame.io?',
        answer:
          'Yes. Dropbox says Replay reviewers don’t need a Dropbox account. Frame.io share reviewers can comment without an account and are asked for a name and email after the first comment.',
      },
      {
        question: 'Can I use Dropbox Replay for free?',
        answer:
          'Partly. Without the add-on you get 4 or 10 file uploads, depending on your Dropbox plan. Frame.io’s Free plan allows 2 members, 2GB (+2GB mounted) and 2 projects.',
      },
      {
        question: 'Where does Kreatli fit compared with Dropbox Replay and Frame.io?',
        answer:
          'Kreatli is a standalone review and production workspace: video, images, PDFs and audio, guest links on every plan, approval statuses guests can see, and tasks and deliverables in the same project. It doesn’t connect to Dropbox and has no editing-software panels.',
      },
    ],
    sourceUrls: [
      { label: 'Dropbox Replay', url: 'https://www.dropbox.com/replay' },
      { label: 'Dropbox help: Replay', url: 'https://help.dropbox.com/installs/dropbox-replay' },
      { label: 'Dropbox help: Replay add-on', url: 'https://help.dropbox.com/installs/dropbox-replay-add-on' },
      ...FRAME_IO_SOURCES,
    ],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
  },
];

export const competitorComparisonPath = (entry: Pick<CompetitorVsEntry, 'slug'>) => `/comparisons/${entry.slug}`;

export function getCompetitorComparison(slug: string): CompetitorVsEntry {
  const entry = COMPETITOR_COMPARISONS.find((item) => item.slug === slug);
  if (!entry) {
    throw new Error(`Unknown competitor comparison: ${slug}`);
  }
  return entry;
}

export const getPublishedCompetitorComparisons = () => COMPETITOR_COMPARISONS.filter(isPublishable);

export const FRAME_IO_FREE_PLAN_LIMITS_PATH = '/comparisons/frame-io-free-plan-limits';
export const BEST_VIDEO_REVIEW_SOFTWARE_PATH = '/comparisons/best-video-review-software';
export const VIDEO_REVIEW_SOFTWARE_PILLAR_PATH = '/platform/review-approval';

export interface ComparisonLink {
  href: string;
  label: string;
}

/**
 * Internal links for a competitor's pricing / vs pages: its pricing page (when published), every published vs
 * page that includes it, the Frame.io free-plan page, the roundup and the pillar. `excludePath` drops the current page.
 */
export function getCompetitorLinks(ids: CompetitorId[], excludePath?: string): ComparisonLink[] {
  const links: ComparisonLink[] = [];
  const pricingEntries: CompetitorPricing[] = ids.map(getCompetitorPricing);

  for (const entry of pricingEntries) {
    if (hasPublishedPricingPage(entry)) {
      links.push({ href: competitorPricingPath(entry), label: `${entry.vendor} pricing` });
    }
  }
  if (ids.includes('frame-io')) {
    links.push({ href: FRAME_IO_FREE_PLAN_LIMITS_PATH, label: 'Frame.io free plan limits' });
  }
  for (const comparison of getPublishedCompetitorComparisons()) {
    if (ids.includes(comparison.a) || ids.includes(comparison.b)) {
      const a = getCompetitorPricing(comparison.a).vendor;
      const b = getCompetitorPricing(comparison.b).vendor;
      links.push({ href: competitorComparisonPath(comparison), label: `${a} vs ${b}` });
    }
  }
  links.push({ href: BEST_VIDEO_REVIEW_SOFTWARE_PATH, label: 'Best video review software (2026)' });
  links.push({ href: VIDEO_REVIEW_SOFTWARE_PILLAR_PATH, label: 'Kreatli video review software' });

  const seen = new Set<string>();
  return links.filter((link) => {
    if (link.href === excludePath || seen.has(link.href)) return false;
    seen.add(link.href);
    return true;
  });
}
